import type { GetTitleRevenueSharesResponse } from "containers/Titles/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "store/store";
import { fetchSubscriptionTitles } from "store/titleStore";

import type { RoyaltyDisplayData, SubscriptionTableRow } from "../../SubscriptionTable/types";

export interface UseSubscriptionTablePaginationProps {
    channelId: number;
    periodId: number;
    totalSubscriptionPoints: number;
    defaultRowsPerPage?: number;
    enabled?: boolean;
    query?: string;
    periodsWithComposites?: Array<{ composite: { id: number }; components: Array<{ id: number }> }>;
}

export interface UseSubscriptionTablePaginationReturn {
    page: number;
    rowsPerPage: number;
    totalCount: number;
    isLoading: boolean;
    data: SubscriptionTableRow[];
    handleChangePage: (_: unknown, newPage: number) => void;
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Converter helper functions
const convertToTableRow = (
    titleData: GetTitleRevenueSharesResponse["titles"][0],
    totalSubscriptionPoints: number,
    royaltiesData: GetTitleRevenueSharesResponse["royalties"],
    currentPeriodId: number,
    periodsWithComposites?: Array<{ composite: { id: number }; components: Array<{ id: number }> }>,
): SubscriptionTableRow => {
    const subscriptionPoints = titleData.subscriptionPoints || 0;

    // Extract royalties based on complex logic
    const titleRoyalties: RoyaltyDisplayData[] = [];
    const titleIdStr = String(titleData.titleId);
    const currentPeriodIdStr = String(currentPeriodId);

    if (royaltiesData[titleIdStr]) {
        // Determine if current period is a composite/parent or component
        const periodComposite = periodsWithComposites?.find((pwc) => pwc.composite.id === currentPeriodId);
        const isCompositePeriod = !!periodComposite;
        // Deduplicate component period IDs to avoid showing the same period multiple times
        const componentPeriodIds = isCompositePeriod
            ? Array.from(new Set(periodComposite.components.map((c) => String(c.id))))
            : [];

        // Find parent period if current is a component
        let parentPeriodId: string | null = null;
        if (!isCompositePeriod) {
            const parentComposite = periodsWithComposites?.find((pwc) =>
                pwc.components.some((c) => c.id === currentPeriodId),
            );
            if (parentComposite) {
                parentPeriodId = String(parentComposite.composite.id);
            }
        }

        // Iterate through all terms for this title
        Object.entries(royaltiesData[titleIdStr]).forEach(([termId, termData]) => {
            const { beneficiaryId, beneficiaryName, beneficiarySurname, periods } = termData;

            const valuesByPeriod: Record<string, { percentage: number; amount: number }> = {};

            if (isCompositePeriod) {
                // Current period is composite/parent
                // Priority: parent period -> component periods (never both)
                if (periods[currentPeriodIdStr]) {
                    // Parent period royalties found - use only parent
                    valuesByPeriod[currentPeriodIdStr] = {
                        percentage: periods[currentPeriodIdStr].percentage,
                        amount: periods[currentPeriodIdStr].amount,
                    };
                } else {
                    // No parent period royalties - use all component periods
                    componentPeriodIds.forEach((componentId) => {
                        if (periods[componentId]) {
                            valuesByPeriod[componentId] = {
                                percentage: periods[componentId].percentage,
                                amount: periods[componentId].amount,
                            };
                        }
                    });
                }
            } else {
                // Current period is component
                // Priority: current component period -> parent period (never both)
                if (periods[currentPeriodIdStr]) {
                    // Current component period royalties found - use only current
                    valuesByPeriod[currentPeriodIdStr] = {
                        percentage: periods[currentPeriodIdStr].percentage,
                        amount: periods[currentPeriodIdStr].amount,
                    };
                } else if (parentPeriodId && periods[parentPeriodId]) {
                    // No current period royalties - use parent period
                    valuesByPeriod[parentPeriodId] = {
                        percentage: periods[parentPeriodId].percentage,
                        amount: periods[parentPeriodId].amount,
                    };
                }
            }

            // Only add term if there are values to display
            if (Object.keys(valuesByPeriod).length > 0) {
                titleRoyalties.push({
                    termId,
                    beneficiaryId,
                    beneficiaryName,
                    beneficiarySurname,
                    valuesByPeriod,
                });
            }
        });
    }

    return {
        id: titleData.titleId,
        title: titleData.titleName || "Unknown Title",
        subtitle: titleData.titleEan13 || "",
        consumptionCount: titleData.consumptionCount || 0,
        avgConsumptionDuration: titleData.avgConsumptionDuration || 0,
        coefficient: titleData.coefficient || 1,
        correctiveFactor: titleData.correctiveFactor || 1,
        totalConsumptionTime: subscriptionPoints,
        totalOfAllItems: totalSubscriptionPoints ?? 0,
        revenueShare: titleData.channelRevenueShare || 0,
        royalties: titleRoyalties,
    };
};

export const useSubscriptionTablePagination = ({
    channelId,
    periodId,
    totalSubscriptionPoints,
    defaultRowsPerPage = 10,
    enabled = true,
    query,
    periodsWithComposites,
}: UseSubscriptionTablePaginationProps): UseSubscriptionTablePaginationReturn => {
    const dispatch = useAppDispatch();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<GetTitleRevenueSharesResponse["titles"]>([]);
    const [royaltiesData, setRoyaltiesData] = useState<GetTitleRevenueSharesResponse["royalties"]>({});
    const [totalCount, setTotalCount] = useState(0);

    const fetchPageData = useCallback(
        async (pageNumber: number, pageSize: number) => {
            if (!enabled) return;

            setIsLoading(true);
            try {
                const result = await dispatch(
                    fetchSubscriptionTitles({
                        channelId,
                        periodId,
                        options: {
                            offset: pageNumber * pageSize,
                            limit: pageSize,
                            query,
                        },
                    }),
                ).unwrap();

                setData(result.titles);
                setRoyaltiesData(result.royalties);
                setTotalCount(result.total);
            } catch (error) {
                console.error("Failed to fetch subscription titles:", error);
                setData([]);
                setRoyaltiesData({});
                setTotalCount(0);
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, channelId, periodId, enabled, query],
    );

    const convertedData = useMemo<SubscriptionTableRow[]>(
        () =>
            data.map((titleData) =>
                convertToTableRow(titleData, totalSubscriptionPoints, royaltiesData, periodId, periodsWithComposites),
            ),
        [data, totalSubscriptionPoints, royaltiesData, periodId, periodsWithComposites],
    );

    // Fetch initial data
    useEffect(() => {
        fetchPageData(page, rowsPerPage);
    }, [fetchPageData, page, rowsPerPage]);

    const handleChangePage = useCallback((_: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    }, []);

    return {
        page,
        rowsPerPage,
        totalCount,
        isLoading,
        data: convertedData,
        handleChangePage,
        handleChangeRowsPerPage,
    };
};
